import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

/**
 * Text us on the church's website (ADR 0074): the setting as `website_text_us` gives it. `available` is whether the
 * church can text at all (its own number, or pilot texting); without it the button cannot be turned on. `live` is
 * whether visitors see it now. `senderName` is who the consent box and every confirmation are signed by.
 */
export interface TextUsSettings {
  readonly enabled: boolean;
  readonly buttonLabel: string;
  readonly greeting: string;
  readonly available: boolean;
  readonly live: boolean;
  readonly website: boolean;
  readonly pilot: boolean;
  readonly senderName: string;
}

const str = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback;

export function toTextUsSettings(raw: unknown): TextUsSettings {
  const d = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: d["enabled"] === true,
    buttonLabel: str(d["buttonLabel"], "Text us"),
    greeting: str(d["greeting"], "Get a quick response via text."),
    available: d["available"] === true,
    live: d["live"] === true,
    website: d["website"] === true,
    pilot: d["pilot"] === true,
    senderName: str(d["senderName"], ""),
  };
}

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** Reads the setting; needs communications.inbox (the database refuses others). */
export function useTextUsSettings() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["text-us", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<TextUsSettings> => {
      const { data, error } = await supabase.rpc("website_text_us", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
      return toTextUsSettings(data);
    },
  });
}

/** Words for a refusal from set_website_text_us: the database's own sentence for a broken rule, ours otherwise. */
export function textUsErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514")
    return error.message.charAt(0).toUpperCase() + error.message.slice(1) + ".";
  if (error.code === "42501") return "Only people who answer the texting inbox can change Text us.";
  return "Text us was not saved. Try again.";
}

/** Saves the setting; answers with the setting as saved. */
export function useSaveTextUs() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      enabled: boolean;
      buttonLabel: string;
      greeting: string;
    }): Promise<TextUsSettings> => {
      const { data, error } = await supabase.rpc("set_website_text_us", {
        p_tenant_id: tenantId ?? "",
        p_enabled: input.enabled,
        p_button_label: input.buttonLabel,
        p_greeting: input.greeting,
      });
      if (error) throw new Error(textUsErrorMessage(error));
      return toTextUsSettings(data);
    },
    onSuccess: (saved) => {
      client.setQueryData(["text-us", tenantId], saved);
    },
  });
}
